/**
 * RerunVersionModal — Launches a fresh debate version under the same query.
 *
 * Conceptual model:
 *   The operator finished a debate, opened the Replay page, looked at the
 *   verdict and asked: "Aynı soruyu farklı kadroyla / daha çok turla /
 *   farklı bir LLM ile çalıştırsam ne olurdu?". This modal is the
 *   answer — a focused single-form submission that produces a brand-new
 *   `sessions` row inheriting the parent's query but stamped with the
 *   operator's fresh configuration.
 *
 * Design choices (Magnum-Opus tier):
 *   1. **Single modal, not a multi-step wizard.** Operators already know
 *      what they want to change (otherwise they'd start from scratch);
 *      forcing them through a 4-step flow buries the action behind clicks.
 *   2. **Prefilled with parent values.** Every input opens populated with
 *      the parent debate's setting so the user only edits deltas. Inertia
 *      defaults beat blank forms every time.
 *   3. **Two paths after submit.**
 *      (a) "Wizard'da incele" → redirect into ConsensusModePage with the
 *          wizard slice prefilled at step 3 (council preview). The user
 *          can fine-tune individual personas before pressing Start. This
 *          path is the safest; no chance of the new run launching with
 *          a hidden config bug.
 *      (b) "Hemen Başlat" (future) — wired here as a TODO; would call
 *          `start()` directly with the freshly-prepared sessionId after
 *          ensuring an audio-context gesture token is in scope. Left as
 *          a guarded TODO because audio-gesture hand-off is touchy and
 *          we don't want to ship a regression to the live debate path.
 *   4. **Model picker uses the live catalogue** (`fetchModelsCatalog`)
 *      with a "Personaların kendi modeli" sentinel so the operator can
 *      explicitly opt out of the global override.
 *   5. **Persona regen toggle**: when on, the wizard's persona generator
 *      will be re-invoked under the parent query. This lets the operator
 *      A/B test "different cast, same question" without starting from
 *      scratch in the wizard.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  X,
  Loader2,
  GitBranch,
  Cpu,
  Users,
  Repeat,
  Thermometer,
  Sparkles,
  ChevronRight,
  AlertCircle,
} from "lucide-react";
import { rerunDebate } from "../../transport/authClient";
import { fetchModelsCatalog } from "../../transport/modelsClient";
import { useDebateStore } from "../../store/debateStore";
import type { ModelCatalogEntry } from "../../domain/types";

interface ParentSnapshot {
  query: string;
  agentCount: number;
  maxRounds: number;
  topology: string;
}

interface Props {
  open: boolean;
  parentDebateId: string;
  parent: ParentSnapshot;
  onClose: () => void;
}

export default function RerunVersionModal({
  open,
  parentDebateId,
  parent,
  onClose,
}: Props) {
  const navigate = useNavigate();
  const prefillFromDebate = useDebateStore((s) => s.prefillFromDebate);

  // Form state — every field starts at the parent's value so the
  // operator only edits intentional deltas.
  const [versionLabel, setVersionLabel] = useState("");
  const [agentCount, setAgentCount] = useState(parent.agentCount);
  const [maxRounds, setMaxRounds] = useState(parent.maxRounds);
  const [modelId, setModelId] = useState<string>("__keep__");
  const [temperature, setTemperature] = useState<number | null>(null);
  const [regeneratePersonas, setRegeneratePersonas] = useState(false);

  // Models catalogue — fetched once per session via the cached transport.
  const [models, setModels] = useState<ModelCatalogEntry[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form whenever the modal is opened against a different parent.
  useEffect(() => {
    if (!open) return;
    setVersionLabel("");
    setAgentCount(parent.agentCount);
    setMaxRounds(parent.maxRounds);
    setModelId("__keep__");
    setTemperature(null);
    setRegeneratePersonas(false);
    setError(null);
    fetchModelsCatalog()
      .then((c) => setModels(c.models))
      .catch(() => setModels([]));
  }, [open, parent.agentCount, parent.maxRounds]);

  // Lock body scroll while the modal is open so background scrolling
  // doesn't bleed through the overlay on long replay pages.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const groupedModels = useMemo(() => {
    const tiers: Record<string, ModelCatalogEntry[]> = {};
    for (const m of models) {
      const key = m.tier ?? "other";
      (tiers[key] ??= []).push(m);
    }
    return tiers;
  }, [models]);

  // Build a one-line "deltas" description so the user can sanity-check
  // what they're about to launch — defends against accidental defaults
  // submission ("oh, I forgot to change the model").
  const deltas: string[] = [];
  if (agentCount !== parent.agentCount)
    deltas.push(`Kişi: ${parent.agentCount} → ${agentCount}`);
  if (maxRounds !== parent.maxRounds)
    deltas.push(`Tur: ${parent.maxRounds} → ${maxRounds}`);
  if (modelId !== "__keep__")
    deltas.push(`Model: → ${models.find((m) => m.id === modelId)?.label ?? modelId}`);
  if (temperature !== null)
    deltas.push(`Sıcaklık: → ${temperature.toFixed(2)}`);
  if (regeneratePersonas) deltas.push("Kadro: Yeniden üretilecek");

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      const opts: Parameters<typeof rerunDebate>[1] = {
        regenerate_personas: regeneratePersonas,
      };
      if (versionLabel.trim()) opts.version_label = versionLabel.trim();
      if (agentCount !== parent.agentCount) opts.agent_count = agentCount;
      if (maxRounds !== parent.maxRounds) opts.max_rounds = maxRounds;
      if (modelId !== "__keep__") opts.model_id = modelId;
      if (temperature !== null) opts.temperature = temperature;

      const res = await rerunDebate(parentDebateId, opts);

      // Prefill the wizard slice so when we land on /debate the
      // wizard opens at step 3 (council preview) with the cloned
      // personas + the freshly-allocated sessionId already wired.
      // The operator just clicks "Başlat" once to launch.
      await prefillFromDebate(res.debate_id, parentDebateId);

      onClose();
      // Navigate to the live-debate page — the wizard is already
      // open from prefillFromDebate so the user lands on the
      // council review screen. One click to start.
      navigate("/debate");
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Yeni versiyon başlatılamadı, ayarları kontrol edip tekrar deneyin.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-violet-500/30 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 shadow-2xl shadow-violet-500/10"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-white/[0.06] bg-slate-950/95 backdrop-blur px-6 py-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-500/15 text-violet-300">
                <GitBranch className="h-4 w-4" aria-hidden />
              </div>
              <h2 className="text-base font-semibold text-white">
                Yeni Versiyonu Çalıştır
              </h2>
            </div>
            <p className="text-[12px] text-slate-400 leading-relaxed">
              Aynı soruyu farklı parametrelerle yeniden müzakere ettir. Yeni
              versiyon, üst tartışmaya bağlı kalır — sonradan AI ile
              karşılaştırabilirsin.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-white/5 disabled:opacity-50"
            aria-label="Kapat"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Source query (read-only) */}
          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
              Üst Tartışmanın Sorusu
            </p>
            <p className="text-[13px] text-slate-200 leading-relaxed">
              {parent.query}
            </p>
          </div>

          {/* Version label */}
          <Field
            label="Versiyon Etiketi (opsiyonel)"
            hint="Bu koşumu siblinglerinden ayıran kısa bir not. Boş bırakılırsa 'v2', 'v3'… verilir."
          >
            <input
              type="text"
              value={versionLabel}
              onChange={(e) => setVersionLabel(e.target.value.slice(0, 80))}
              placeholder="örn. Daha küçük kadro, Risk-ağırlıklı, Qwen vs Gemma"
              className="w-full rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-2 text-[13px] text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-violet-500/50 focus:bg-white/[0.05]"
              disabled={submitting}
            />
          </Field>

          {/* Numeric grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <NumberField
              label="Kişi Sayısı"
              icon={<Users className="h-3.5 w-3.5" aria-hidden />}
              hint={`Üstteki: ${parent.agentCount}`}
              value={agentCount}
              min={3}
              max={20}
              onChange={setAgentCount}
              disabled={submitting}
            />
            <NumberField
              label="Tur Sayısı"
              icon={<Repeat className="h-3.5 w-3.5" aria-hidden />}
              hint={`Üstteki: ${parent.maxRounds}`}
              value={maxRounds}
              min={2}
              max={50}
              onChange={setMaxRounds}
              disabled={submitting}
            />
          </div>

          {/* Model picker */}
          <Field
            label="LLM (Tüm üyelere uygulanır)"
            hint="Persona kadrosu ile farklı modelin nasıl performans gösterdiğini test etmek için A/B koşumu yap."
            icon={<Cpu className="h-3.5 w-3.5" aria-hidden />}
          >
            <select
              value={modelId}
              onChange={(e) => setModelId(e.target.value)}
              disabled={submitting}
              className="w-full rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-2 text-[13px] text-slate-200 focus:outline-none focus:border-violet-500/50"
            >
              <option value="__keep__">
                Personaların kendi modelini koru (önerilen)
              </option>
              {Object.entries(groupedModels).map(([tier, list]) => (
                <optgroup
                  key={tier}
                  label={
                    tier === "fast"
                      ? "Hızlı"
                      : tier === "balanced"
                      ? "Dengeli"
                      : tier === "deep"
                      ? "Derin Muhakeme"
                      : "Diğer"
                  }
                >
                  {list.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.label} ({m.family})
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </Field>

          {/* Temperature */}
          <Field
            label="Sıcaklık (opsiyonel global override)"
            hint="0 = deterministik, 1 = dengeli, 2 = yüksek divergens. Boş bırakılırsa modelin kendi varsayılanı kullanılır."
            icon={<Thermometer className="h-3.5 w-3.5" aria-hidden />}
          >
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={0}
                max={2}
                step={0.05}
                value={temperature ?? 0.7}
                onChange={(e) => setTemperature(parseFloat(e.target.value))}
                disabled={submitting}
                className="flex-1 accent-violet-500"
              />
              <span className="w-14 text-right font-mono text-[12px] text-slate-300">
                {temperature === null ? "—" : temperature.toFixed(2)}
              </span>
              {temperature !== null && (
                <button
                  type="button"
                  onClick={() => setTemperature(null)}
                  disabled={submitting}
                  className="text-[11px] text-slate-400 hover:text-white"
                >
                  Sıfırla
                </button>
              )}
            </div>
          </Field>

          {/* Persona regen toggle */}
          <label
            className={`block rounded-lg border p-3 cursor-pointer transition ${
              regeneratePersonas
                ? "border-violet-500/40 bg-violet-500/[0.06]"
                : "border-white/[0.08] bg-white/[0.02] hover:border-white/[0.14]"
            }`}
          >
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={regeneratePersonas}
                onChange={(e) => setRegeneratePersonas(e.target.checked)}
                disabled={submitting}
                className="mt-1 h-4 w-4 rounded border-white/20 bg-white/5 text-violet-500 focus:ring-violet-500"
              />
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <Sparkles className="h-3.5 w-3.5 text-violet-300" aria-hidden />
                  <span className="text-[13px] font-semibold text-white">
                    Kadroyu Sıfırdan Üret
                  </span>
                </div>
                <p className="text-[11.5px] text-slate-400 leading-relaxed">
                  Açık: Wizard, üst sorudan tamamen yeni bir uzman kadrosu
                  yaratır. <em>Aynı soru, farklı bakış açıları</em> A/B'si
                  için. Kapalı: üstteki kadro birebir kopyalanır (önerilen).
                </p>
              </div>
            </div>
          </label>

          {/* Deltas summary */}
          {deltas.length > 0 && (
            <div className="rounded-lg border border-violet-500/30 bg-violet-500/[0.04] p-3">
              <p className="text-[10px] uppercase tracking-wider text-violet-300 font-semibold mb-1.5">
                Bu Çalıştırmada Değişen Ayarlar
              </p>
              <ul className="space-y-0.5">
                {deltas.map((d, i) => (
                  <li
                    key={i}
                    className="text-[12px] text-violet-100 flex items-center gap-1.5"
                  >
                    <ChevronRight className="h-3 w-3 text-violet-400" aria-hidden />
                    {d}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 rounded-lg border border-red-500/40 bg-red-500/[0.06] p-3">
              <AlertCircle className="h-4 w-4 text-red-400 mt-0.5 shrink-0" aria-hidden />
              <p className="text-[12px] text-red-200 leading-relaxed">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 z-10 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 border-t border-white/[0.06] bg-slate-950/95 backdrop-blur px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-[13px] text-slate-300 hover:text-white hover:bg-white/[0.04] disabled:opacity-50"
          >
            İptal
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-5 py-2.5 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white text-[13px] font-semibold shadow-lg shadow-violet-500/30 hover:shadow-violet-500/50 hover:from-violet-400 hover:to-fuchsia-400 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Versiyon Hazırlanıyor…
              </>
            ) : (
              <>
                <GitBranch className="h-4 w-4" aria-hidden />
                Yeni Versiyonu Hazırla & Wizard'da Aç
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────── Field primitives ─────────────────────── */

function Field({
  label,
  hint,
  icon,
  children,
}: {
  label: string;
  hint?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-300 mb-1.5">
        {icon}
        {label}
      </label>
      {children}
      {hint && (
        <p className="mt-1.5 text-[11px] text-slate-500 leading-relaxed">
          {hint}
        </p>
      )}
    </div>
  );
}

function NumberField({
  label,
  icon,
  hint,
  value,
  min,
  max,
  onChange,
  disabled,
}: {
  label: string;
  icon?: React.ReactNode;
  hint?: string;
  value: number;
  min: number;
  max: number;
  onChange: (n: number) => void;
  disabled?: boolean;
}) {
  return (
    // exactOptionalPropertyTypes: omit `hint` / `icon` entirely when
    // they are undefined rather than passing them through (which would
    // mismatch the strict prop type).
    <Field
      label={label}
      {...(hint !== undefined ? { hint } : {})}
      {...(icon !== undefined ? { icon } : {})}
    >
      <div className="flex items-center gap-2">
        <input
          type="range"
          min={min}
          max={max}
          step={1}
          value={value}
          onChange={(e) => onChange(parseInt(e.target.value, 10))}
          disabled={disabled}
          className="flex-1 accent-violet-500"
        />
        <input
          type="number"
          min={min}
          max={max}
          value={value}
          onChange={(e) => {
            const v = parseInt(e.target.value, 10);
            if (Number.isFinite(v))
              onChange(Math.min(max, Math.max(min, v)));
          }}
          disabled={disabled}
          className="w-16 rounded-md border border-white/[0.08] bg-white/[0.03] px-2 py-1 text-center font-mono text-[12px] text-slate-200 focus:outline-none focus:border-violet-500/50"
        />
      </div>
    </Field>
  );
}
